/**
 * matchQuestionsTypes - MatchQuestions 卡片与弹窗共享的类型和排序规则。
 * 排序：clicks 降序；clicks 相同时 createTime 早的排前。
 * createTime 可能是 "YYYY-MM-DD HH:mm:ss" 字符串，也可能是毫秒时间戳（Long）。
 * queId 是后端的雪花 ID（19 位），超过 JS Number 精度，必须以字符串形式保留。
 */

export interface MatchQuestionItem {
  answer: string
  clicks: number
  question: string
  queId: string
  createBy?: string
  createTime?: string | number
  avatar: string
}

export interface MatchQuestionsPayload {
  items: MatchQuestionItem[]
  messageId: string
}

export type OpenMatchQuestionsFn = (payload: MatchQuestionsPayload) => void

// 在 JSON.parse 之前把 "queId": <大整数> 改写成 "queId": "<大整数>"，避免精度丢失。
// 只匹配紧跟冒号的裸数字，不影响已经带引号的情况。
const preserveQueIdPrecision = (raw: string): string =>
  raw.replace(/"queId"\s*:\s*(\d+)/g, '"queId":"$1"')

export const parseMatchQuestions = (raw: string): MatchQuestionItem[] => {
  try {
    const parsed: unknown = JSON.parse(preserveQueIdPrecision(raw))
    if (!Array.isArray(parsed)) return []
    return parsed.map((item) => {
      const it = item as Record<string, unknown>
      return {
        answer: typeof it.answer === 'string' ? it.answer : '',
        clicks: typeof it.clicks === 'number' ? it.clicks : 0,
        question: typeof it.question === 'string' ? it.question : '',
        queId: typeof it.queId === 'string' ? it.queId : String(it.queId ?? ''),
        avatar: typeof it.avatar === 'string' ? it.avatar : '',
        createBy: typeof it.createBy === 'string' ? it.createBy : undefined,
        createTime:
          typeof it.createTime === 'string' || typeof it.createTime === 'number'
            ? it.createTime
            : undefined,
      }
    })
  } catch {
    return []
  }
}

const parseCreateTime = (raw?: string | number): number => {
  if (raw === undefined || raw === null || raw === '') return Number.POSITIVE_INFINITY
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : Number.POSITIVE_INFINITY
  const normalized = raw.replace(' ', 'T')
  const t = Date.parse(normalized)
  return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY
}

export const sortMatchQuestions = (items: MatchQuestionItem[]): MatchQuestionItem[] => {
  return [...items].sort((a, b) => {
    const clickDiff = (b.clicks ?? 0) - (a.clicks ?? 0)
    if (clickDiff !== 0) return clickDiff
    return parseCreateTime(a.createTime) - parseCreateTime(b.createTime)
  })
}

const pad2 = (n: number) => (n < 10 ? `0${n}` : String(n))

export const formatMatchQuestionDate = (raw?: string | number): string => {
  if (raw === undefined || raw === null || raw === '') return ''
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) return ''
    const d = new Date(raw)
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
  }
  const datePart = raw.slice(0, 10)
  if (/^\d{4}[-/.]\d{2}[-/.]\d{2}$/.test(datePart)) {
    return datePart.replace(/[-/]/g, '-')
  }
  return raw
}
